import { Button, DataTable, Form, Modal, ResultsCard, ResultsToolbar, Tab, Tabs } from 'shared/components/ui';
import React, { useState, useEffect, useCallback } from 'react';
import { MedicalServices, UnfoldMore, Save, ArrowBack } from '@mui/icons-material';

import { Search, Clear } from '@mui/icons-material';
import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import { Toast } from '../../../shared/components/Toast';
import { estudiosMedicosAPI, centrosAPI } from '../../../core/api/api';
import { useSortableTable } from '../../../shared/hooks/useSortableTable';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const PAGE_SIZE = 50;

const ESTADO_COLORES = {
  'Cancelado': { bg: '#ef4444', text: '#fff' },
  'No Realizado': { bg: '#ef4444', text: '#fff' },
  'Realizado': { bg: '#22c55e', text: '#fff' },
  'Revisar': { bg: '#16a34a', text: '#fff' },
  'Listo para Enviar': { bg: '#d946ef', text: '#fff' },
  'Enviado': { bg: '#f97316', text: '#fff' },
};

const EMPTY_HISTORIA = {
  motivoConsulta: '', escolaridad: '', antecedentesFamiliares: '',
  embarazo: '', parto: '', eg: '', pn: '', apgar: '', fei: '',
  internaciones: '', desarrolloMadurativo: '', sueno: '', otrosAntecedentes: '',
  examenFisico: '', medicacionRecibe: '', examenComplementario: '',
  diagnosticoPresunto: '', conducta: '',
};

const hoy = new Date();
const hace80 = new Date(hoy); hace80.setDate(hoy.getDate() - 80);
const fmt = (d) => d.toISOString().substring(0, 10);

const GrillaEstudiosMedicos = () => {
  const [turnos, setTurnos] = useState([]);
  const [centros, setCentros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(1);

  const [filtros, setFiltros] = useState({
    desde: fmt(hace80), hasta: fmt(hoy),
    idCentro: '', idEstadoTurno: '', nombre: '',
  });

  // Modal
  const [turnoSeleccionado, setTurnoSeleccionado] = useState(null);
  const [historia, setHistoria] = useState(EMPTY_HISTORIA);
  const [consultasAnteriores, setConsultasAnteriores] = useState([]);
  const [acordeonAbierto, setAcordeonAbierto] = useState(null);
  const [tabActiva, setTabActiva] = useState('paciente');
  const [guardando, setGuardando] = useState(false);

  const { sortedData, requestSort, getSortIcon } = useSortableTable(turnos);

  useEffect(() => {
    centrosAPI.getCentros().then(r => setCentros(r.data || [])).catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        desde: filtros.desde + 'T00:00:00',
        hasta: filtros.hasta + 'T23:59:59',
        ...(filtros.idCentro && { idCentro: filtros.idCentro }),
        ...(filtros.idEstadoTurno && { idEstadoTurno: filtros.idEstadoTurno }),
        ...(filtros.nombre && { nombre: filtros.nombre }),
      };
      const res = await estudiosMedicosAPI.getTurnos(params);
      setTurnos(res.data || []);
      setPage(1);
    } catch {
      setToast({ message: 'Error al cargar los turnos.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { cargar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.ceil(sortedData.length / PAGE_SIZE);
  const paginados = sortedData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleEditar = async (t) => {
    try {
      const res = await estudiosMedicosAPI.getTurno(t.idHorario);
      const data = res.data;
      setTurnoSeleccionado(data);
      setHistoria({
        motivoConsulta: data.motivoConsulta || '',
        escolaridad: data.escolaridad || '',
        antecedentesFamiliares: data.antecedentesFamiliares || '',
        embarazo: data.embarazo || '',
        parto: data.parto || '',
        eg: data.eg || '',
        pn: data.pn || '',
        apgar: data.apgar || '',
        fei: data.fei || '',
        internaciones: data.internaciones || '',
        desarrolloMadurativo: data.desarrolloMadurativo || '',
        sueno: data.sueno || '',
        otrosAntecedentes: data.otrosAntecedentes || '',
        examenFisico: data.examenFisico || '',
        medicacionRecibe: data.medicacionRecibe || '',
        examenComplementario: data.examenComplementario || '',
        diagnosticoPresunto: data.diagnosticoPresunto || '',
        conducta: data.conducta || '',
      });
      setTabActiva('paciente');
      setAcordeonAbierto(null);

      if (data.idPersona && data.idTurno) {
        const ant = await estudiosMedicosAPI.getConsultasAnteriores(t.idHorario, data.idPersona, data.idTurno);
        setConsultasAnteriores(ant.data || []);
      }
    } catch {
      setToast({ message: 'Error al cargar el turno.', type: 'error' });
    }
  };

  const handleGuardarHistoria = async () => {
    if (!turnoSeleccionado?.idTurno) return;
    setGuardando(true);
    try {
      await estudiosMedicosAPI.guardarHistoria(turnoSeleccionado.idTurno, historia);
      setToast({ message: 'Historia clínica guardada con éxito.', type: 'success' });
      setTurnoSeleccionado(null);
      cargar();
    } catch {
      setToast({ message: 'Error al guardar la historia clínica.', type: 'error' });
    } finally {
      setGuardando(false);
    }
  };

  const f = (k) => (e) => setFiltros(p => ({ ...p, [k]: e.target.value }));
  const h = (k) => (e) => setHistoria(p => ({ ...p, [k]: e.target.value }));

  const formatFechaHora = (f) => f ? new Date(f).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '';

  const CampoHistoria = ({ label, campo, multiline = true }) => (
    <Form.Group className="mb-3">
      <Form.Label style={{ fontWeight: '600', fontSize: '0.85rem', color: '#475569' }}>{label}</Form.Label>
      <Form.Control
        as={multiline ? 'textarea' : 'input'}
        rows={multiline ? 2 : undefined}
        value={historia[campo]}
        onChange={h(campo)}
        style={{ fontSize: '0.875rem' }}
      />
    </Form.Group>
  );

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <MedicalServices sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Estudios Médicos
          </h2>
          <span className="page-subtitle">Consultas y historia clínica</span>
        </div>

        {/* Modal */}
        <Modal show={!!turnoSeleccionado} onHide={() => setTurnoSeleccionado(null)} size="xl" dialogClassName="modal-estudios-medicos">
          <style>{`
            .modal-estudios-medicos .modal-content { max-width: none !important; width: 100% !important; }
            .modal-estudios-medicos { max-width: 1000px !important; width: 90vw !important; }
            .modal-estudios-medicos .nav-tabs .nav-link { color: #495057 !important; font-size: 0.95rem; padding: 0.5rem 1rem; }
            .modal-estudios-medicos .nav-tabs .nav-link.active { color: #6d1212 !important; font-weight: 700; border-bottom: 3px solid #6d1212; background: none; }
          `}</style>
          <Modal.Header closeButton>
            <Modal.Title>
              {turnoSeleccionado?.apellido}, {turnoSeleccionado?.nombre} — {formatFechaHora(turnoSeleccionado?.fechaIni)}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Tabs activeKey={tabActiva} onSelect={setTabActiva} className="mb-3">

              {/* Tab Paciente */}
              <Tab eventKey="paciente" title="Datos del Paciente">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem', padding: '0.5rem 0' }}>
                  {[
                    ['Tipo Doc.', turnoSeleccionado?.tipoDni],
                    ['DNI', turnoSeleccionado?.dni],
                    ['Apellido', turnoSeleccionado?.apellido],
                    ['Nombre', turnoSeleccionado?.nombre],
                    ['Fecha Nac.', turnoSeleccionado?.fechaNacimiento ? new Date(turnoSeleccionado.fechaNacimiento).toLocaleDateString('es-AR') : ''],
                    ['Celular', turnoSeleccionado?.telCelular],
                    ['Obra Social', turnoSeleccionado?.obraSocial],
                    ['Correo', turnoSeleccionado?.mail],
                    ['Dirección', turnoSeleccionado?.direccion],
                    ['Localidad', turnoSeleccionado?.localidad],
                    ['Estudio', turnoSeleccionado?.tipoEstudio],
                    ['Fecha Turno', formatFechaHora(turnoSeleccionado?.fechaIni)],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} style={{ background: '#f8fafc', borderRadius: '6px', padding: '0.5rem 0.75rem' }}>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>{label}</div>
                      <div style={{ fontSize: '0.875rem', color: '#1e293b' }}>{value}</div>
                    </div>
                  ))}
                </div>
              </Tab>

              {/* Tab Consultas Anteriores */}
              <Tab eventKey="anteriores" title={`Consultas Anteriores (${consultasAnteriores.length})`}>
                {consultasAnteriores.length === 0 ? (
                  <p style={{ color: '#64748b', textAlign: 'center', padding: '1rem' }}>No hay consultas anteriores.</p>
                ) : consultasAnteriores.map((c, i) => (
                  <div key={i} style={{ border: '1px solid #dee2e6', borderRadius: '6px', marginBottom: '0.5rem' }}>
                    <button
                      onClick={() => setAcordeonAbierto(acordeonAbierto === i ? null : i)}
                      style={{ width: '100%', textAlign: 'left', padding: '0.6rem 1rem', background: acordeonAbierto === i ? '#f8f9fa' : 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between' }}
                    >
                      <span>{i + 1}) {formatFechaHora(c.inicio)} — {c.tipoEstudio} — {c.medico}</span>
                      <span>{acordeonAbierto === i ? '▲' : '▼'}</span>
                    </button>
                    {acordeonAbierto === i && (
                      <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid #dee2e6', fontSize: '0.8rem' }}>
                        {[
                          ['Médico', c.medico], ['Motivo Consulta', c.motivoConsulta],
                          ['Escolaridad', c.escolaridad], ['Antecedentes Familiares', c.antecedentesFamiliares],
                          ['Embarazo', c.embarazo], ['Parto', c.parto], ['EG', c.eg], ['PN', c.pn],
                          ['APGAR', c.apgar], ['FEI', c.fei], ['Internaciones', c.internaciones],
                          ['Sueño', c.sueno], ['Otros Antecedentes', c.otrosAntecedentes],
                          ['Examen Físico', c.examenFisico], ['Medicación', c.medicacionRecibe],
                          ['Exámenes Complementarios', c.examenComplementario],
                          ['Diagnóstico Presunto', c.diagnosticoPresunto], ['Conducta', c.conducta],
                        ].filter(([, v]) => v).map(([label, value]) => (
                          <p key={label} style={{ margin: '0.2rem 0' }}><strong>{label}:</strong> {value}</p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </Tab>

              {/* Tab Historia Clínica */}
              <Tab eventKey="historia" title="Historia Clínica">
                <div style={{ columns: '2', columnGap: '1.5rem' }}>
                  <div style={{ breakInside: 'avoid' }}>
                    <CampoHistoria label="Motivo de Consulta" campo="motivoConsulta" />
                    <CampoHistoria label="Escolaridad" campo="escolaridad" />
                    <CampoHistoria label="Antecedentes Familiares" campo="antecedentesFamiliares" />
                    <CampoHistoria label="Embarazo" campo="embarazo" multiline={false} />
                    <CampoHistoria label="Parto" campo="parto" multiline={false} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.5rem' }}>
                      {[['EG', 'eg'], ['PN', 'pn'], ['APGAR', 'apgar'], ['FEI', 'fei']].map(([l, k]) => (
                        <Form.Group key={k} className="mb-3">
                          <Form.Label style={{ fontWeight: '600', fontSize: '0.85rem', color: '#475569' }}>{l}</Form.Label>
                          <Form.Control value={historia[k]} onChange={h(k)} style={{ fontSize: '0.875rem' }} />
                        </Form.Group>
                      ))}
                    </div>
                    <CampoHistoria label="Internaciones" campo="internaciones" />
                  </div>
                  <div style={{ breakInside: 'avoid' }}>
                    <CampoHistoria label="Desarrollo Madurativo" campo="desarrolloMadurativo" />
                    <CampoHistoria label="Sueño" campo="sueno" />
                    <CampoHistoria label="Otros Antecedentes Relevantes" campo="otrosAntecedentes" />
                    <CampoHistoria label="Examen Físico" campo="examenFisico" />
                    <CampoHistoria label="Medicación que Recibe" campo="medicacionRecibe" />
                    <CampoHistoria label="Exámenes Complementarios" campo="examenComplementario" />
                    <CampoHistoria label="Diagnóstico Presunto" campo="diagnosticoPresunto" />
                    <CampoHistoria label="Conducta" campo="conducta" />
                  </div>
                </div>
              </Tab>
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setTurnoSeleccionado(null)} disabled={guardando}>
              <ArrowBack sx={{ fontSize: 16, mr: 0.5 }} /> Cerrar
            </Button>
            {tabActiva === 'historia' && (
              <Button variant="success" onClick={handleGuardarHistoria} disabled={guardando}>
                <Save sx={{ fontSize: 16, mr: 0.5 }} /> Guardar Historia
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        {/* Filtros */}
        <div className="filters-card">
          <div className="filters-header"><h3 className="filters-title">Filtros de Búsqueda</h3></div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${filtros.desde ? 'filter-active' : ''}`}>
                <label className="filter-label">Desde</label>
                <input type="date" className="filter-input" value={filtros.desde} onChange={f('desde')} />
              </div>
              <div className={`filter-group ${filtros.hasta ? 'filter-active' : ''}`}>
                <label className="filter-label">Hasta</label>
                <input type="date" className="filter-input" value={filtros.hasta} onChange={f('hasta')} />
              </div>
              <div className={`filter-group ${filtros.idCentro ? 'filter-active' : ''}`}>
                <label className="filter-label">Centro</label>
                <select className="filter-input" value={filtros.idCentro} onChange={f('idCentro')}>
                  <option value="">Centro...</option>
                  {centros.map(c => <option key={c.idCentro} value={c.idCentro}>{c.centro}</option>)}
                </select>
              </div>
              <div className={`filter-group ${filtros.nombre ? 'filter-active' : ''}`}>
                <label className="filter-label">Paciente / DNI</label>
                <input type="text" className="filter-input" placeholder="dni, nombre..." value={filtros.nombre} onChange={f('nombre')} onKeyDown={e => e.key === 'Enter' && cargar()} />
              </div>
              <div className="filter-actions">
                <Button className="btn-search" onClick={cargar} disabled={loading}>
                  <Search sx={{ fontSize: 18, mr: 0.5 }} /> Buscar
                </Button>
                <Button className="btn-clear" onClick={() => { setFiltros({ desde: fmt(hace80), hasta: fmt(hoy), idCentro: '', idEstadoTurno: '', nombre: '' }); }}>
                  <Clear sx={{ fontSize: 18, mr: 0.5 }} /> Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla */}
        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left"><h3 className="results-title">Resultados</h3></div>
            <div className="results-info">
              <span className="results-count">{turnos.length} turnos encontrados</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('idHorario')} style={{ cursor: 'pointer' }}># {getSortIcon('idHorario') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}</th>
                  <th onClick={() => requestSort('apeNombre')} style={{ cursor: 'pointer' }}>Paciente {getSortIcon('apeNombre') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}</th>
                  <th>Edad</th>
                  <th>Teléfono</th>
                  <th onClick={() => requestSort('tipoEstudio')} style={{ cursor: 'pointer' }}>Estudio {getSortIcon('tipoEstudio') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}</th>
                  <th onClick={() => requestSort('fechaIni')} style={{ cursor: 'pointer' }}>Fecha {getSortIcon('fechaIni') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}</th>
                  <th>Horario</th>
                  <th>Obra Social</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginados.length === 0 ? (
                  <tr><td colSpan="10" className="empty-state">{loading ? 'Cargando...' : 'No hay turnos para mostrar'}</td></tr>
                ) : paginados.map(t => {
                  const estadoStyle = ESTADO_COLORES[t.estadoTurno] || {};
                  return (
                    <tr key={t.idHorario}>
                      <td>{t.numero || t.idHorario}</td>
                      <td>{t.apeNombre}</td>
                      <td>{t.edad}</td>
                      <td>{t.telCelular}</td>
                      <td style={t.color && !estadoStyle.bg ? { background: t.color, color: t.color === '#ffff00' ? '#000' : '#fff', borderRadius: '4px', padding: '2px 6px' } : {}}>{t.tipoEstudio}</td>
                      <td>{t.fechaIni ? new Date(t.fechaIni).toLocaleDateString('es-AR') : ''}</td>
                      <td>{t.horaIni}</td>
                      <td>{t.obraSocial}</td>
                      <td>
                        {t.estadoTurno && (
                          <span style={{ background: estadoStyle.bg || '#e2e8f0', color: estadoStyle.text || '#475569', borderRadius: '4px', padding: '2px 8px', fontSize: '0.8rem', fontWeight: '600' }}>
                            {t.estadoTurno}
                          </span>
                        )}
                      </td>
                      <td>
                        <Button variant="primary" size="sm" className="btn-edit" sx={{ bgcolor: '#3b82f6 !important', color: '#fff !important', '&:hover': { bgcolor: '#2563eb !important' } }} onClick={() => handleEditar(t)}>Editar</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </DataTable>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '1rem' }}>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(1)} disabled={page === 1}>«</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</Button>
              <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Página {page} de {totalPages}</span>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>›</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</Button>
            </div>
          )}
        </ResultsCard>
      </div>
      <Footer />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaEstudiosMedicos;
